import { useParams, Link, Navigate } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seoMeta";
import { IMAGES } from "@/lib/site";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/common/CTASection";
import { Button } from "@/components/ui/button";
import { MARKET_CONTENT, MARKET_CONTENT_AR } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";

export default function MarketPage() {
  const { market } = useParams();
  const { L, isAr } = useLang();
  const c = (isAr ? MARKET_CONTENT_AR : MARKET_CONTENT)[market];
  useSeo({ title: c?.title, description: c?.description, canonical: `${SITE_URL}/markets/${market}`, image: IMAGES.skyline, locale: isAr ? "ar_AE" : "en_US" });
  if (!c) return <Navigate to="/markets" replace />;

  return (
    <>
      <PageHero
        overline={c.overline}
        title={c.title}
        description={c.description}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: L.pages.markets.overline, to: "/markets" }, { label: c.overline }]}
      />

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-12 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-10">
            {c.sections.map((s) => (
              <div key={s.h}>
                <h2 className="font-heading text-2xl font-semibold text-ink">{s.h}</h2>
                <p className="mt-3 leading-relaxed text-subtle">{s.p}</p>
              </div>
            ))}
            <div className="rounded-lg border border-hair bg-canvas p-5 text-sm text-subtle">
              Related learning:{" "}
              <Link to="/education" className="font-semibold text-finblue">Education Hub</Link>{" · "}
              <Link to="/market-analysis" className="font-semibold text-finblue">Market Analysis</Link>{" · "}
              <Link to="/economic-calendar" className="font-semibold text-finblue">Economic Calendar</Link>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-xl border border-hair bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-semibold text-ink">Example Instruments</h3>
              <ul className="mt-4 space-y-2">
                {c.instruments.map((i) => (
                  <li key={i} className="flex items-center justify-between border-b border-hair pb-2 text-sm">
                    <span className="font-medium text-ink">{i}</span>
                    <span className="text-xs text-subtle">via partner</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-subtle">Availability depends on the brokerage partner and your jurisdiction.</p>
            </div>
            <div className="rounded-xl border border-hair bg-navy p-6 text-white shadow-sm">
              <h3 className="font-heading text-lg font-semibold">Access this market</h3>
              <p className="mt-2 text-sm text-slate-300">Compare brokerage partners offering {c.overline.toLowerCase()} instruments.</p>
              <Button asChild className="mt-4 w-full bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{c.cta}</Link></Button>
            </div>
          </aside>
        </div>
      </section>

      <CTASection />
    </>
  );
}
